import * as React from 'react'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'

import { buttonVariants } from 'src/components/ui/button'
import { LeftOutlined, RightOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `Calendar` primitive, brought in with `npx shadcn@latest add
 * calendar` (react-day-picker v10). It backs ONE seam and nothing else: the
 * shadcn half of `src/antd/DatePicker`. Nothing reaches it until someone flips
 * the design toggle, and `react-day-picker` may be named in THIS FILE AND
 * NOWHERE ELSE — the narrowing is ABSOLUTE, values and types alike, which is
 * why the `Matcher` the seam's `panel.tsx` needs is re-exported at the foot of
 * this file rather than imported from the package there. Same narrowing
 * `recharts` and `@tanstack/react-table` carry, and it is enforced, not
 * reviewed: `src/antd/DatePicker/__tests__/datepicker-one-door.test.ts`.
 *
 * IT RENDERS THE DAY GRID ONLY. Everything date-library-shaped the app cares
 * about — parsing, formatting, the active locale, the month and weekday names,
 * the week numbers, the first day of the week — is supplied by the SEAM from
 * dayjs, through `formatters` / `weekStartsOn` / `components` props (see
 * `src/antd/DatePicker/panel.tsx`). react-day-picker's own date-fns locale
 * data is never consulted for anything a user reads, so the app keeps one date
 * library.
 *
 * Edits to the CLI's output, each of them a decision:
 *
 * 1. THE CHEVRONS COME FROM `src/icons/antd-compat`, not `lucide-react`.
 *    `components.json` says `iconLibrary: lucide`, so the CLI writes raw
 *    lucide imports; on this branch the icon seam is the only place that
 *    specifier may appear (AGENTS.md), and routing through it is also what
 *    makes these two glyphs follow the design toggle like every other icon.
 *    `LeftOutlined`/`RightOutlined` are the antd names the seam maps onto
 *    lucide's `ChevronLeft`/`ChevronRight`.
 * 2. `CalendarDayButton` is DROPPED. The CLI's day button is a shadcn
 *    `Button` carrying the whole selected/range/focus treatment; the seam
 *    replaces `components.DayButton` outright so it can render the antd
 *    `cellRender`/`dateRender` payload a call site passed. Keeping the CLI's
 *    version would ship a second, never-rendered copy of that treatment for a
 *    future reader to "fix".
 * 3. For the same reason the `buttonVariant` prop is dropped: the only thing
 *    still styled by `buttonVariants` here is the nav pair, which the seam
 *    also hides (`components.Nav`) because navigation lives in the seam's own
 *    panel header, shared by both panels of a range.
 *
 * What is deliberately KEPT even though the seam overrides it: the full
 * `classNames` map. Those are react-day-picker's slot names, not dead code —
 * anything the seam does not override still renders through them.
 * ------------------------------------------------------------------------- */

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-background p-3 [--cell-size:--spacing(8)] [[data-slot=popover-content]_&]:bg-transparent',
        className
      )}
      captionLayout={captionLayout}
      formatters={formatters}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months
        ),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption
        ),
        caption_label: cn(
          'text-sm font-medium select-none',
          defaultClassNames.caption_label
        ),
        month_grid: cn('w-full border-collapse', defaultClassNames.month_grid),
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 rounded-md text-[0.8rem] font-normal text-muted-foreground select-none',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn(
          'w-(--cell-size) select-none',
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          'text-[0.8rem] text-muted-foreground select-none',
          defaultClassNames.week_number
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-md',
          props.showWeekNumber
            ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-md'
            : '[&:first-child[data-selected=true]_button]:rounded-l-md',
          defaultClassNames.day
        ),
        range_start: cn(
          'rounded-l-md bg-accent',
          defaultClassNames.range_start
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('rounded-r-md bg-accent', defaultClassNames.range_end),
        today: cn(
          'rounded-md bg-accent text-accent-foreground data-[selected=true]:rounded-none',
          defaultClassNames.today
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            ref={rootRef}
            className={cn(rootClassName)}
            {...rootProps}
          />
        ),
        Chevron: ({ orientation }) =>
          orientation === 'left' ? <LeftOutlined /> : <RightOutlined />,
        ...components,
      }}
      {...props}
    />
  )
}

export { Calendar }

/**
 * The one type the seam needs from the package. Re-exported here so the ONE
 * DOOR above holds for types too — see `panel.tsx`, which takes it from this
 * module. A type re-export is erased at build time and ships no bytes.
 */
export type { Matcher } from 'react-day-picker'
